import {LoadSpacingScenario} from '@hconnect/tuc-processes/fields/loadSpacing/types'
import {useLoadSpacing} from '@hconnect/tuc-processes/fields/loadSpacing/useLoadSpacing'
import {useFormEffect} from '@hconnect/tuc-processes/form/useFormEffect'
import {useIsOrderChange} from '@hconnect/tuc-processes/orderManagement/orderChange'

import {useEnhancedLoadSpacingScenario} from './useEnhancedLoadSpacingScenario'

/**
 * Enhanced load spacing adjustment hook that uses the country-specific enhanced scenario
 * instead of the base scenario from tuc-processes package.
 *
 * This ensures that for countries like GB, both spacing and timeOnJobSite can be shown
 * simultaneously according to country features.
 */
export const useEnhancedLoadSpacingAdjustment = () => {
  const {
    spacingOrFlowRate,
    setSpacingOrFlowRate,
    defaultSpacingOrFlowRate,
    timeOnJobSite,
    setTimeOnJobSite,
    defaultTimeOnJobSite,
    preserveSpacingOrFlowRate,
    loadSpacingConfiguration
  } = useLoadSpacing()
  const currentScenario = useEnhancedLoadSpacingScenario()
  const isOrderChange = useIsOrderChange()

  // When editing an existing order we must only restore the order's own timeOnJobSite value.
  // Orders where SAP auto-calculates it (e.g. DE/BE/NL multi-truck) intentionally have no value,
  // so falling back to the configuration default would inject a phantom change the user never made.
  // During order intake the configuration default is the desired starting value.
  const resolveTimeOnJobSiteDefault = (): number | undefined => {
    if (isOrderChange) {
      return defaultTimeOnJobSite?.time
    }
    const configDefault = loadSpacingConfiguration.isEnabled
      ? loadSpacingConfiguration.timeOnJobSite?.default
      : undefined
    return defaultTimeOnJobSite?.time ?? configDefault
  }

  const setSpacingOrFlowRateWithDefaults = (type: 'loadSpace' | 'flowRate') => {
    const currentValue = spacingOrFlowRate?.type === type ? spacingOrFlowRate[type] : undefined
    const defaultValue =
      defaultSpacingOrFlowRate?.type === type ? defaultSpacingOrFlowRate[type] : undefined
    const newValue = currentValue ?? defaultValue ?? loadSpacingConfiguration[type]?.default
    setSpacingOrFlowRate(newValue as number, type)
  }

  const adjustTimeOnJobSite = (scenario: LoadSpacingScenario) => {
    if (!loadSpacingConfiguration.isEnabled) {
      return
    }

    // For enhanced scenarios (like GB), we want to ensure timeOnJobSite is set
    // when the scenario requires it, even if it was cleared by spacing logic
    if (scenario.timeOnJobSite) {
      if (!timeOnJobSite) {
        setTimeOnJobSite(resolveTimeOnJobSiteDefault())
      }
    } else if (!scenario.timeOnJobSite && timeOnJobSite) {
      setTimeOnJobSite(undefined)
    }
  }

  const adjustSpacingOrFlowRate = (scenario: LoadSpacingScenario) => {
    if (preserveSpacingOrFlowRate) {
      return
    }

    if (scenario.loadSpace || scenario.flowRate) {
      const hasValidValue =
        (scenario.loadSpace && spacingOrFlowRate?.type === 'loadSpace') ||
        (scenario.flowRate && spacingOrFlowRate?.type === 'flowRate')
      if (hasValidValue) {
        return
      }

      // For the enhanced scenario (e.g., GB), prioritize spacing if both are available
      // This ensures we don't accidentally default to flowRate when spacing is preferred
      if (scenario.loadSpace) {
        setSpacingOrFlowRateWithDefaults('loadSpace')
      } else if (scenario.flowRate) {
        setSpacingOrFlowRateWithDefaults('flowRate')
      }
    } else if (spacingOrFlowRate) {
      setSpacingOrFlowRate(undefined)
    }
  }

  useFormEffect(() => {
    adjustSpacingOrFlowRate(currentScenario)
    adjustTimeOnJobSite(currentScenario)
  }, [currentScenario, preserveSpacingOrFlowRate])

  // Separate effect to ensure timeOnJobSite is maintained when scenario requires it
  // This runs after any changes to timeOnJobSite or spacingOrFlowRate and restores
  // timeOnJobSite if it was cleared by the underlying spacing logic
  useFormEffect(() => {
    if (
      currentScenario.timeOnJobSite &&
      !timeOnJobSite &&
      loadSpacingConfiguration.isEnabled &&
      spacingOrFlowRate?.type === 'loadSpace'
    ) {
      setTimeOnJobSite(resolveTimeOnJobSiteDefault())
    }
  }, [
    currentScenario.timeOnJobSite,
    timeOnJobSite,
    spacingOrFlowRate,
    loadSpacingConfiguration.isEnabled
  ])
}
